import React, { useEffect } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Input
} from "@material-ui/core";
import { v4 as uuidv4 } from "uuid";
import inputComponentMask from "lib/inputMask";
import styled from "styled-components";

import { groupBy } from "../..";
import "./styles.css";

const StyledInput = styled(Input)`text-align-last: center;`;

const CreditAdjustments = ({
  constraints,
  setConstraints,
  constraintsByLoanType,
  spreads,
  setSpreads,
  spreadsByLoanTypeSettingId,
  sponsorSpreadsByLoanTypeSettingId,
  sponsorConstraintsByLoanType,
  isBroker
}) => {
  useEffect(() => {
    const groupedSpreads = isBroker ? groupBy(sponsorSpreadsByLoanTypeSettingId, s => s.termMonths) : groupBy(spreadsByLoanTypeSettingId, s => s.termMonths);
    const sortedConstraintsIds = isBroker ? sponsorConstraintsByLoanType?.sort((a, b) => b.ltv - a.ltv)?.map(({ id }, idx) => ({ constraintId: id, order: idx })) : constraintsByLoanType?.sort((a, b) => b.ltv - a.ltv)?.map(({ id }, idx) => ({ constraintId: id, order: idx }));

    const newSpreads = Array.from(groupedSpreads, ([name, value]) => ({
      term: name,
      loanProceedsSpreads: value,
      spreadRowId: uuidv4(),
      amount: [...value]
    }))?.map(ar => ({
      ...ar,
      amount: ar?.amount?.map(({ spread, loanProceedsCreditAdjustment, underwritingConstraintId }, idx) => ({
        constraintId: underwritingConstraintId,
        constraintOrder: sortedConstraintsIds.find(constraint => constraint?.constraintId === underwritingConstraintId)?.order,
        spread,
        term: ar.term,
        creditAdjustment: loanProceedsCreditAdjustment?.spreadAdjustment
      }))?.filter(({ constraintId }) => constraintId !== undefined)?.sort((a, b) => a.constraintOrder - b.constraintOrder)
    }))?.map(y => ({
      ...y,
      amount: y.amount.map(z => {
        const spreadId = y?.loanProceedsSpreads.find(x => x.underwritingConstraintId === z.constraintId);

        return ({
          ...z,
          spreadId: spreadId?.id
        });
      })
    })).sort((a, b) => a.term - b.term);
    setSpreads(newSpreads);
  }, [spreadsByLoanTypeSettingId, sponsorSpreadsByLoanTypeSettingId]);

  useEffect(() => {
    const sortedConstraints = (sponsorConstraintsByLoanType.length ? sponsorConstraintsByLoanType : constraintsByLoanType)?.map(({ id, ltvMax, debtYieldMin, dscrMin }) => ({
      constraintId: id,
      ltv: ltvMax,
      dy: debtYieldMin,
      dscr: dscrMin
    })).sort((a, b) => b.ltv - a.ltv);

    setConstraints(sortedConstraints);
  }, [constraintsByLoanType]);

  const handleCreditAdjustmentValueChange = ({ value }, constraintId, spreadRowId) => {
    const valFloat = typeof value === "string" ? parseFloat(value.replace("%", "").trim()) : 0;
    const newSpreads = spreads.map(spread => (spread.spreadRowId === spreadRowId ?
      {
        ...spread,
        amount: spread.amount.map(a => ((a.constraintId === constraintId) ?
          { ...a, creditAdjustment: valFloat / 100, term: spread.term } :
          { ...a }))
      } :
      { ...spread }));
    setSpreads(newSpreads);
  };

  return (
    <Table className="credit-adjustments-table-wrapper">
      <TableHead className="credit-adjustments-table-header">
        <TableRow>
          <TableCell className="credit-adjustments-table-header-constraint-table-cell" align="center">Constraints</TableCell>
          {constraints.map(constraint => <TableCell align="center">
            <div className="credit-adjustments-table-header-cell-wrapper">
              <div>
                <div>
                  {Math.floor(constraint.ltv * 100)}
                  % LTV
                </div>
                <div>
                  {constraint.dscr}
                  x DSCR
                </div>
                <div>
                  {(constraint.dy * 100).toFixed(2)}
                  % DY
                </div>
              </div>
            </div>
          </TableCell>)}
        </TableRow>
      </TableHead>
      <TableBody className="credit-adjustments-body-section-wrapper">
        {spreads?.map((spread, idx) => <TableRow key={idx}>
          <TableCell className="credit-adjustments-body-first-column-cell">
            <div>
              {spread.term}
              {" "}
              Years
            </div>
          </TableCell>
          {spread?.amount?.map(({ constraintId = "", creditAdjustment }, idx) => <TableCell key={idx}>
            <div className="credit-adjustments-body-spread-amount-cell">
              <StyledInput
                id={`cred-adjustment-${spread?.spreadRowId}-${constraintId || ""}`}
                name={`cred-adjustment-${spread?.spreadRowId}-${constraintId || ""}`}
                decimals={2}
                onChange={({ target }) => handleCreditAdjustmentValueChange(target, constraintId || "", spread?.spreadRowId)}
                value={creditAdjustment * 100 || 0}
                suffix="%"
                inputComponent={inputComponentMask({ mask: "percentmasknf" })}
              />
            </div>
          </TableCell>)}
        </TableRow>)}
      </TableBody>
    </Table>
  );
};

export default CreditAdjustments;
